import type { ReactElement } from 'react';

import classNames from 'classnames';

import { makeUrl } from '@iconik-io/core/src/url/make_url';
import type { AssetsSegmentSchemaWithIdPatched } from '@iconik-io/iconik-api/src/schema_patched/assets_segments';
import {
  Button,
  CopyButton,
  Icon,
  Menu,
  type MenuItemWithId,
  MenuTrigger,
  Tooltip,
  TooltipTrigger,
  useCanHover,
} from '@iconik-io/uad';

import { DeleteSegment } from './delete_segment';

import styles from './segment_actions.module.css';

const getSegmentLabel = (segment: AssetsSegmentSchemaWithIdPatched) => {
  let label: string | undefined;
  if (segment.segment_type === 'COMMENT') {
    label = 'comment';
  }
  if (segment.segment_type === 'GENERIC' || segment.segment_type === 'TAG') {
    label = 'time-based metadata';
  }
  return label;
};

function CopySegmentLinkButton({
  segment,
}: {
  segment: AssetsSegmentSchemaWithIdPatched;
}) {
  return (
    <CopyButton
      value={makeUrl({
        search: {
          segment_ids: [segment.id],
        },
      })}
      label={`Copy link to this ${getSegmentLabel(segment) ?? 'segment'}`}
      size="s"
      iconName="link"
      animationTimeout={1500}
    />
  );
}

type SegmentActionProps = {
  isEditActive?: boolean;
  editButtonDisabled?: boolean;
  showActions: boolean;
  segment: AssetsSegmentSchemaWithIdPatched;
  canEdit: boolean;
  canDelete: boolean;
  onDelete: () => void;
  onEdit: () => void;
  editButtonPrependElement?: ReactElement;
};
export function SegmentActions({ ...props }: SegmentActionProps) {
  const hasDeviceHoverSupport = useCanHover();
  const {
    isEditActive,
    editButtonDisabled,
    showActions,
    segment,
    canEdit,
    onDelete,
    editButtonPrependElement,
    onEdit,
    canDelete,
  } = props;

  const actionElementClassnames = classNames(styles.hover_actions, {
    [styles.hover_actions_hidden]: !showActions,
  });

  // We want to show a menu of actions if the user can not hover (i.e. on mobile devices)
  if (!hasDeviceHoverSupport) {
    if (!canEdit && !canDelete) {
      return <CopySegmentLinkButton segment={segment} />;
    }

    const actionMenuItems: MenuItemWithId[] = [
      {
        label: `Copy link to this ${getSegmentLabel(segment) ?? 'segment'}`,
        id: 'copy_link_to_comment',
        icon: 'link',
        onAction: () => {
          navigator.clipboard.writeText(
            makeUrl({
              search: {
                segment_ids: [segment.id],
              },
            }),
          );
        },
      },
    ];
    if (canEdit) {
      actionMenuItems.push({
        label: `Edit this ${getSegmentLabel(segment) ?? 'segment'}`,
        id: 'edit_comment',
        icon: 'edit',
        onAction: () => onEdit(),
      });
    }
    if (canDelete) {
      actionMenuItems.push({
        label: `Delete this ${getSegmentLabel(segment) ?? 'segment'}`,
        id: 'delete_comment',
        icon: 'deletion_delete',
        onAction: () => onDelete(),
      });
    }

    return (
      <MenuTrigger>
        <Button
          aria-label="More actions"
          size="s"
          variant="tertiary"
          icon={<Icon name="menu_context" />}
        />
        <Menu
          selectionMode="none"
          items={actionMenuItems}
          onSelectionChange={(keys) => {
            const selectedKey =
              Array.isArray(keys) || keys instanceof Set
                ? Array.from(keys)[0]
                : keys;
            const actionItem = actionMenuItems.find(
              (item) => item.id === selectedKey,
            );
            if (actionItem?.onAction) {
              actionItem.onAction();
            }
          }}
        />
      </MenuTrigger>
    );
  }

  // Otherwise, we want to show the action buttons on hover.
  if (isEditActive && canDelete) {
    return (
      <DeleteSegment
        editButtonDisabled={editButtonDisabled}
        onDelete={onDelete}
      />
    );
  } else {
    return (
      <>
        <div className={actionElementClassnames}>
          <CopySegmentLinkButton segment={segment} />
        </div>

        {canEdit ? (
          <div className={actionElementClassnames}>
            <TooltipTrigger>
              {editButtonPrependElement}
              <Button
                aria-label="Edit comment"
                size="s"
                variant="tertiary"
                onPress={onEdit}
                icon={<Icon name="edit" />}
                isDisabled={editButtonDisabled}
              />
              <Tooltip>Edit comment</Tooltip>
            </TooltipTrigger>
          </div>
        ) : null}
      </>
    );
  }
}
